import React, {useCallback, useEffect, useState} from "react";

import {
    Box,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import {VersionItem} from "../../Components/VersionItem";
import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
import {useApplicationContext} from "../../ApplicationContext";
import {VersionTreeNodeDto} from "../../apiClient/Api";
import {MarkdownRenderer} from "../../Components/MarkdownRenderer";
import {useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCircleInfo} from '@fortawesome/pro-solid-svg-icons';

type RouteParams = {
    link: string
}

type GroupHeaderProps = {
    version: VersionTreeNodeDto
    label: string
    stickyTop: number
    level: 'major' | 'minor'
}

const VersionGroupHeader = ({version, label, stickyTop, level}: GroupHeaderProps) => {
    const theme = useTheme();
    const isMajor = level === 'major';
    const bg = theme.palette.mode === 'dark'
        ? (isMajor ? theme.palette.background.paper : 'rgba(255,255,255,0.03)')
        : (isMajor ? theme.palette.background.paper : 'rgba(0,0,0,0.02)');

    const scrollToVersion = () => {
        const el = document.getElementById(version.versionId!);
        if (el) el.scrollIntoView({behavior: 'smooth'});
    };

    return (
        <Box sx={{position: 'sticky', top: stickyTop, zIndex: isMajor ? 3 : 2}}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderTop: isMajor ? `2px solid ${theme.palette.primary.main}` : 'none',
                    backgroundColor: bg,
                    backdropFilter: 'saturate(180%) blur(8px)',
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        px: {xs: 1.5, sm: 2.5},
                        py: 0.75,
                        minHeight: isMajor ? 44 : 36,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                    }}
                    onClick={scrollToVersion}
                >
                    <Typography
                        variant={isMajor ? "subtitle1" : "subtitle2"}
                        color={isMajor ? "primary" : "text.primary"}
                        sx={{fontWeight: 700, lineHeight: 1.2, letterSpacing: 0.3}}
                    >
                        {label}
                    </Typography>
                </Stack>
            </Paper>
        </Box>
    );
};

type GroupNotesProps = {
    notes?: string | null
    level: 'major' | 'minor'
}

const VersionGroupNotes = ({notes, level}: GroupNotesProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    if (!notes || notes.length === 0) return null;
    const isMajor = level === 'major';

    return (
        <Box
            sx={{
                px: {xs: 2, sm: 3},
                py: 1.5,
                mt: 1,
                borderRadius: 1.5,
                backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.025)'
                    : 'rgba(0,0,0,0.02)',
                borderLeft: `3px solid ${isMajor ? theme.palette.primary.main : theme.palette.divider}`
            }}
        >
            <Typography
                variant="overline"
                color="text.secondary"
                sx={{fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5}}
            >
                <FontAwesomeIcon icon={faCircleInfo} fontSize={12}/>
                {t('version.item.notes')}
            </Typography>
            <MarkdownRenderer>{notes}</MarkdownRenderer>
        </Box>
    );
};

export const Versions = () => {
    useDocumentTitle("base.pages.versions");
    const {t} = useTranslation();
    const [data, setData] = useState<VersionTreeNodeDto[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const context = useApplicationContext();
    const params = useParams<RouteParams>();
    const controller = context.api.version;

    const parseVersionFromLink = (link: string): { productId: string; versionKey?: { major: number; minor: number; patch: number; productId: string } } | null => {
        const versionLinkMatch = link.match(/^V(\d+)\.(\d+)\.(\d+)_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
        if (versionLinkMatch) {
            return {
                productId: versionLinkMatch[4],
                versionKey: {
                    major: parseInt(versionLinkMatch[1]),
                    minor: parseInt(versionLinkMatch[2]),
                    patch: parseInt(versionLinkMatch[3]),
                    productId: versionLinkMatch[4]
                }
            };
        }
        return {productId: link};
    };

    const fetchData = useCallback(async () => {
        if (!params.link) return;
        setIsFetching(true);
        try {
            const parsed = parseVersionFromLink(params.link);
            if (!parsed) return;

            if (parsed.versionKey) {
                const value = context.isInternal
                    ? await controller.versionGetVersionByKey(parsed.versionKey)
                    : await controller.versionGetVersionByKeyForCustomer(parsed.versionKey);
                if (value && value.data) {
                    setData([value.data as unknown as VersionTreeNodeDto]);
                }
            } else {
                const value = context.isInternal
                    ? await controller.versionGetVersions(parsed.productId)
                    : await controller.versionGetCustomerVersions(parsed.productId);
                if (value && value.data) {
                    setData(value.data);
                }
            }
        } catch (error) {
            context.apiError?.(error);
        } finally {
            setIsFetching(false);
        }
    }, [params.link, context, controller]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (window.location.hash && data.length > 0) {
            const versionId = window.location.hash.substring(1);
            const element = document.getElementById(versionId);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({behavior: 'smooth', block: 'start'});
                }, 300);
            }
        }
    }, [data]);

    const reloadCallback = useCallback(async () => {
        await fetchData();
    }, [fetchData]);

    const MAJOR_STICKY_TOP = 64;
    const MINOR_STICKY_TOP = MAJOR_STICKY_TOP + 44;

    return (
        <Box sx={{width: '100%', padding: {xs: 1, sm: 2}}}>
            {isFetching ? (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2}}>
                    <CircularProgress size={48}/>
                    <Typography variant="body2" color="text.secondary">
                        {t('versions.loading')}
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={3}>
                    {data.map((major) => (
                        <Box key={'versionElement_' + major.versionId}>
                            <VersionGroupHeader
                                version={major}
                                label={t('version.item.versionNumber', {version: major.major})}
                                stickyTop={MAJOR_STICKY_TOP}
                                level="major"
                            />
                            <VersionGroupNotes notes={major.header} level="major"/>
                            <Stack spacing={2} sx={{mt: 1.5, pl: {xs: 0, sm: 1}}}>
                                {major.childVersions?.map((minor) => (
                                    <Box key={'minorBlock_' + minor.versionId}>
                                        <VersionGroupHeader
                                            version={minor}
                                            label={t('version.item.versionNumber', {version: `${major.major}.${minor.minor}`})}
                                            stickyTop={MINOR_STICKY_TOP}
                                            level="minor"
                                        />
                                        <VersionGroupNotes notes={minor.header} level="minor"/>
                                        <Stack spacing={1.5} sx={{mt: 1, pl: {xs: 0, sm: 1}}}>
                                            {minor.childVersions?.map((patch) => (
                                                <VersionItem data={patch} reloadCallback={reloadCallback} key={patch.versionId}/>
                                            ))}
                                            <VersionItem data={minor} reloadCallback={reloadCallback}/>
                                        </Stack>
                                    </Box>
                                ))}
                                <VersionItem data={major} reloadCallback={reloadCallback}/>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
};
